import React, { useState, useEffect } from "react";
import { updateLocationRanking } from "services/rankingService";
import { Pencil } from "lucide-react";
import * as Sentry from "@sentry/react";

const PlayerRankings = ({
  locationRankings = [],
  loading = false,
  isAdmin = false,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [editedPlayer, setEditedPlayer] = useState({});
  const [localRankings, setLocalRankings] = useState([]);

  useEffect(() => {
    setLocalRankings(locationRankings);
  }, [locationRankings]);

  const openModal = (player) => {
    setCurrentPlayer(player);
    setEditedPlayer({ playerName: player.playerName, ranking: player.ranking });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setCurrentPlayer(null);
    setEditedPlayer({});
  };

  const handleSave = async () => {
    try {
      await updateLocationRanking(currentPlayer.id, editedPlayer);

      window.location.reload(); // ✅ reload page after save
    } catch (error) {
      Sentry.captureException(error);
      console.error("Erreur lors de la mise à jour du classement :", error);
      alert("Échec de la mise à jour.");
    }
  };

  if (loading) {
    return (
      <p className="text-center text-slate-200">Chargement du classement...</p>
    );
  }

  if (localRankings.length === 0) {
    return (
      <p className="text-center text-slate-400">Aucun classement disponible.</p>
    );
  }

  return (
    <div className="w-full text-slate-50">
      <div className="max-h-[600px] overflow-y-auto">
        <table className="w-full text-xs lg:text-sm bg-slate-800 rounded overflow-hidden">
          <thead>
            <tr className="text-amber-400">
              <th className="px-1 py-1 text-center">#</th>
              <th className="px-1 py-1 text-left">Joueur</th>
              <th className="px-1 py-1 text-center">Elo</th>
              {isAdmin && <th className="px-1 py-1 text-center"></th>}
            </tr>
          </thead>
          <tbody>
            {localRankings.map((player, idx) => (
              <tr key={player.id} className="border-t border-slate-700">
                <td className="px-1 py-0.5 text-center font-bold text-amber-400">
                  {idx + 1}
                </td>
                <td className="px-1 py-0.5 truncate max-w-[140px]">
                  {player.playerName}
                </td>
                <td className="px-1 py-0.5 text-center font-bold text-emerald-400">
                  {player.ranking}
                </td>
                {isAdmin && (
                  <td className="px-1 py-0.5 text-center">
                    <button
                      onClick={() => openModal(player)}
                      className="text-slate-400 hover:text-slate-200 p-1"
                    >
                      <Pencil size={12} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            className="bg-slate-800 border border-slate-700 p-6 rounded shadow-xl w-full max-w-md space-y-4"
          >
            <h3 className="text-xl font-bold text-slate-50">
              Modifier le classement
            </h3>

            <div>
              <label className="block text-sm mb-1 text-slate-400">Nom</label>
              <input
                type="text"
                value={editedPlayer.playerName}
                onChange={(e) =>
                  setEditedPlayer({
                    ...editedPlayer,
                    playerName: e.target.value,
                  })
                }
                required
                className="w-full px-3 py-2 bg-slate-800 text-slate-50 rounded border border-slate-700"
              />
            </div>

            <div>
              <label className="block text-sm mb-1 text-slate-400">
                Classement
              </label>
              <input
                type="number"
                value={editedPlayer.ranking}
                onChange={(e) =>
                  setEditedPlayer({
                    ...editedPlayer,
                    ranking: parseInt(e.target.value, 10),
                  })
                }
                className="w-full px-3 py-2 bg-slate-800 text-slate-50 rounded border border-slate-700"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-800/50 text-slate-50 rounded"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded"
              >
                Sauvegarder
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default PlayerRankings;
